import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface NewVerification {
  id: string;
  user_id: string;
  email_verified: boolean;
  email_verification_code: string | null;
  email_verified_at: string | null;
  verification_codes_expires_at: string | null;
  status: string;
  rejection_reason: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  selfie_photo_url: string | null;
  ai_analysis: any | null;
  selfie_verified: boolean;
  selfie_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export const useNewVerification = () => {
  const [verification, setVerification] = useState<NewVerification | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const getCurrentVerification = useCallback(async (): Promise<NewVerification | null> => {
    if (!user?.id) return null;

    try {
      const { data, error } = await supabase
        .from('document_verifications')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching verification:', error);
        return null;
      }

      setVerification(data);
      return data;
    } catch (error) {
      console.error('Error in getCurrentVerification:', error);
      return null;
    }
  }, [user?.id]);

  const createVerification = useCallback(async (): Promise<NewVerification | null> => {
    if (!user?.id) return null;

    try {
      // Verificar se já existe uma verificação
      const existing = await getCurrentVerification();
      if (existing) {
        return existing;
      }

      const { data, error } = await supabase
        .from('document_verifications')
        .insert({
          user_id: user.id,
          email_verified: false,
          selfie_verified: false,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating verification:', error);
        return null;
      }

      setVerification(data);
      return data;
    } catch (error) {
      console.error('Error in createVerification:', error);
      return null;
    }
  }, [user?.id, getCurrentVerification]);

  const updateVerification = useCallback(async (
    verificationId: string, 
    updates: Partial<NewVerification>
  ): Promise<NewVerification | null> => {
    try {
      const { data, error } = await supabase
        .from('document_verifications')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', verificationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating verification:', error);
        return null;
      }

      setVerification(data);
      return data;
    } catch (error) {
      console.error('Error in updateVerification:', error);
      return null;
    }
  }, []);

  return {
    verification,
    isLoading,
    getCurrentVerification,
    createVerification,
    updateVerification,
    setIsLoading
  };
};